import { webappPortal } from "./webapp-portal";

/** End-user web app (mockups/app/login.html + chat.html, docs/plan/screens-auth-app-widget.md §2). */
export const webapp = {
  tenant: {
    notFoundTitle: "This assistant isn't available here",
    notFoundBody: "Check the link from your organization, or open the address they shared with you.",
    errorTitle: "We couldn't load the assistant",
    errorBody: "Check your connection and try again.",
  },
  find: {
    title: "Find your organization",
    body: "Open the link your organization shared with you, or enter the name from the end of that link.",
    label: "Organization link name",
    placeholder: "your-organization",
    hint: "Example: {{example}}",
    submit: "Open assistant",
    invalid: "Use letters, numbers and hyphens only.",
  },
  language: {
    label: "Language",
    en: "English",
    fil: "Filipino",
    setEn: "Language set to English",
    setFil: "Filipino na ang wika",
  },
  login: {
    siteLinkHint: "Opens {{host}}",
    tagline: "The official AI assistant of {{org}}",
    badgeLanguages: "English · Filipino · Taglish",
    badgeOpen: "Open 24/7",
    guest: "Continue as guest",
    guestBody: "Public answers only — services, pricing, schedules",
    divider: "Members",
    portal: "Sign in with your organization portal",
    portalUnavailable: "Organization portal sign-in isn't set up for this assistant yet.",
    google: "Sign in with Google",
    otp: "Sign in with email code",
    privacy: "Conversations are processed by {{org}} under its privacy notice and kept for {{months}} months. Don't share passwords or bank details in chat.",
    privacyLink: "Privacy Notice",
    privacyToast: "The Privacy Notice of {{org}} opens here once it is published.",
    help: "Trouble signing in?",
    helpLink: "Contact the team",
    helpToast: "Contact {{org}} through their official channels for sign-in help.",
    poweredBy: "Powered by {{product}}",
    copyright: "© {{year}} {{org}}",
    announcementDismiss: "Dismiss announcement",
  },
  otp: {
    emailLabel: "Email address",
    emailPlaceholder: "you@example.com",
    emailError: "Enter a valid email address.",
    emailHint: "Use the email address registered with {{org}}.",
    send: "Email me a code",
    sentToast: "If this email is registered, a code is on its way — valid for 10 minutes",
    sentTo: "We sent a 6-digit code to",
    change: "Change",
    digit: "Digit {{n}}",
    resendIn: "Resend in {{time}}",
    resend: "Resend code",
    resentToast: "New code sent",
    verify: "Verify & continue",
    codeError: "Enter the 6-digit code from the email.",
    unavailable: "Email sign-in isn't available yet. Continue as guest for public answers.",
    failed: "We couldn't verify that code. Check it and try again.",
  },
  sidebar: {
    label: "Chats",
    close: "Close menu",
    open: "Open menu",
    newChat: "New chat",
    search: "Search chats",
    history: "Chat history",
    groups: {
      pinned: "Pinned",
      today: "Today",
      yesterday: "Yesterday",
      previous7: "Previous 7 days",
      older: "Older",
    },
    emptyTitle: "No chats yet",
    emptyBody: "Your questions appear here so you can pick up where you left off.",
    noMatches: "No chats match “{{query}}”.",
    guestNote: "Guest chats are saved on this device only. Sign in to keep your history everywhere.",
    options: "Chat options",
    rename: "Rename",
    renamePrompt: "Chat name",
    renameSave: "Save name",
    pin: "Pin",
    unpin: "Unpin",
    delete: "Remove from this device",
    deleted: "Chat removed from this device",
    deleteServer: "Delete chat",
    deletedServer: "Chat deleted",
    transcript: "Download transcript",
    transcriptDone: "Transcript downloaded (.txt)",
    transcriptFailed: "We couldn't download the transcript. Please try again.",
    loadError: "We couldn't load your chats.",
    loadMore: "Show older chats",
  },
  announcements: {
    title: "Announcements",
    newCount_one: "{{count}} new",
    newCount_other: "{{count}} new",
    urgent: "Urgent",
    pinned: "Announcement",
    expires: "{{app}} will answer questions about this announcement until it expires on {{date}}.",
    notify: "Notify me of updates",
    notifyToast: "Email alerts for updates aren't available yet — check back here for changes.",
    close: "Close",
  },
  session: {
    signedIn: "You're signed in",
    ssoFailed: "We couldn't sign you in with your organization portal. Please try again from the portal.",
  },
  user: {
    menu: "Account menu",
    member: "Signed in",
    verified: "Verified",
    signedInAs: "Signed in as",
    signOut: "Sign out",
    signedOut: "You're signed out",
    guest: "Guest",
    guestRole: "Public answers only",
    guestNote: "You are not signed in. Sign in for members-only answers.",
    signIn: "Sign in",
    theme: "Toggle theme",
    language: "Language",
    languageAuto: "Auto",
    languageToast: "Language follows your messages — English, Filipino or Taglish",
  },
  yourData: {
    download: "Download my data",
    erase: "Delete my data",
    downloaded: "Your data was downloaded",
    downloadFailed: "We couldn't prepare your data. Please try again.",
    rateLimited: "You've tried that a few times. Please wait a while before trying again.",
    eraseTitle: "Delete your data?",
    eraseIntro: "This permanently deletes, for every assistant in {{org}}'s chat:",
    eraseConversations: "your conversations and the feedback you gave on answers",
    eraseContact: "the name, email and phone number you shared, and your answers in forms",
    eraseKeep: "Enquiries and bookings you already sent stay with {{org}}. This can't be undone, and this browser starts fresh.",
    eraseSignedIn: "You stay signed in.",
    eraseConfirm: "Delete my data",
    eraseCancel: "Keep my data",
    erased: "Your data was deleted",
    eraseFailed: "We couldn't delete your data. Please try again.",
  },
  header: {
    chooseAssistant: "Choose an assistant",
    assistantMenu: "Assistant: {{name}}",
    switched: "Switched to {{name}}",
    publicTag: "Open to everyone",
    share: "Share",
    shareUnavailable: "Sharing links is coming soon.",
    newChat: "New chat",
    verified: "Verified",
    verifiedHint: "Answers can include member-only info",
  },
  welcome: {
    greeting: "Hi {{name}}! How can I help today?",
    greetingGuest: "Hi there! How can I help today?",
    sub: "Ask in English, Filipino or Taglish. I answer from {{org}}'s official documents and show you the source.",
    subGuest: "You are chatting as a guest, so I only use public information. Sign in to see member-only answers.",
    suggestions: "Suggested questions",
    categories: {
      requirements: "Requirements and what to submit",
      fees: "Fees and payment options",
      scholarships: "Scholarships and discounts",
      schedule: "Dates and schedules",
      documents: "Documents and requests",
      visit: "Visits and appointments",
      general: "Ask the assistant",
    },
  },
  composer: {
    label: "Message",
    placeholder: "Ask a question…",
    attach: "Attach file",
    attachToast: "Attachments aren't turned on for this assistant yet.",
    voice: "Voice input",
    voiceToast: "Voice input is coming soon — type in English or Filipino for now.",
    send: "Send",
    stop: "Stop generating",
    enterToSend: "to send",
    newLine: "new line",
    disclaimer: "Answers come from official {{org}} documents. Always confirm fees with the office concerned.",
    connecting: "Connecting to the assistant…",
  },
  chat: {
    log: "Conversation",
    jump: "Jump to latest",
    loadingTranscript: "Loading conversation",
    transcriptErrorTitle: "We couldn't open this chat",
    transcriptErrorBody: "It may have been started on another device or browser.",
    startNew: "Start a new chat",
    visitorErrorTitle: "We couldn't connect to the assistant",
    visitorErrorBody: "Check your connection and try again.",
    unsent: "Not sent · Tap to retry",
    handoffConnecting: "Connecting you to the {{team}}…",
    handoffConnectingGeneric: "Connecting you to a staff member…",
    handoffQueued: "You're in the queue for {{team}}",
    handoffJoined: "{{name}} ({{role}}) joined the conversation",
    handoffResolved: "This conversation was resolved by staff",
    handoffAiBack: "AI assistant is back",
    handoffWaitingTitle: "Waiting for the team",
    handoffWaitingTeam: "{{team}} will reply here shortly",
    handoffWaitingGeneric: "The team will reply here shortly",
    queuePositionNext: "You're next in line",
    queuePosition: "You're number {{position}} in line",
    queueWaitRange: "Usually about {{low}}–{{high}} minutes",
    queueWaitUnder: "Usually under a minute",
    queueSoon: "A teammate will join soon",
    staffTyping: "{{name}} is typing…",
    staff: "Staff",
    // While an answer is prepared (rotates every 2.5 s). Never says how it is found: no searching, documents or sources.
    status: {
      thinking: "Thinking…",
      working: "Working on it…",
      composing: "Putting that together…",
      almost: "Almost there…",
    },
    errors: {
      generic: "Something went wrong on our side. Please try again.",
      network: "Connection lost. Check your internet and try again.",
      rateLimited: "The assistant is busy — try again in a minute.",
      unsupported: "This browser couldn't receive the answer. Please try again.",
      retry: "Try again",
    },
    actions: {
      retry: "Try again",
      sending: "Sending…",
      detailsHeading: "Your details",
      required: "This field is required",
      invalidEmail: "Enter a valid email address",
      invalidMobile: "Enter the number with its country code, e.g. +63 917 123 4567",
      contactRule: "Add an email or a phone number so we can reach you",
      phoneHint: "Optional · include your country code",
      choose: "Choose…",
      consent: "I agree to the privacy notice",
      privacyLink: "Read the privacy notice",
      consentRequired: "Please agree to the privacy notice to continue",
      checkField: "Check this field",
      leadSubmit: "Send my details",
      requestIntro: "Pick a time and send your details. The team confirms it by email — it isn't booked until they do.",
      calendarIntro: "Pick a time and it's booked right away.",
      requestThisTime: "Request this time",
      bookThisTime: "Book this time",
      minutes: "{{count}} min",
      yourTime: "Your time",
      pickATime: "Pick a time first",
      chooseDay: "Choose a day",
      chooseTime: "Choose a time",
      loadingTimes: "Loading available times",
      timesIn: "Times are shown in your time zone ({{zone}})",
      dayChip_one: "{{date}}, {{count}} time open",
      dayChip_other: "{{date}}, {{count}} times open",
      showMoreDays: "Show more days",
      loadingMoreDays: "Loading more days",
      noTimesTitle: "No open times in these days",
      noTimesBody: "Try later dates.",
      nextOpen: "The next open time is {{when}}.",
      jumpTo: "Jump to {{date}}",
      slotsError: "We couldn't load the available times.",
      addToCalendar: "Add to calendar",
      joinMeet: "Join the video call",
      stateSent: "Sent",
      stateRequested: "Not confirmed yet",
      stateConfirmed: "Confirmed",
      stateFailed: "Not completed",
      completed: "Done",
      expired: "This request expired — ask again",
      unavailable: "This request is no longer available — ask again",
      slotTaken: "That time was just taken. Please pick another.",
      validation: "Check the highlighted details and try again.",
      rateLimited: "Too many tries. Try again in {{seconds}} s.",
      rateLimitedNoWait: "Too many tries. Please wait a moment and try again.",
      serviceUnavailable: "This is temporarily unavailable. Please try again in a moment.",
      conflict: "This was already sent or is still being processed. Check your email, or ask again.",
      captcha: "Please wait for the security check to finish, then try again.",
      signIn: "Please sign in to continue.",
      generic: "Something went wrong. Please try again.",
      previewNote: "Preview — actions run for real visitors",
      previewTimes: "Open times appear here for real visitors.",
    },
  },
  portal: webappPortal,
} as const;
